<!--Copy right - © 2024 tecera gmbh-->
<script>
    import { onMount } from "svelte";
    import {
        selected_device,
        current_device_data,
        chart,
        current_sample_time,
        extended_device_channel_list,
        current_device_data_additional_values,
    } from "../stores.js";
    import Highcharts from 'highcharts/highstock';
    import HighchartsMore from 'highcharts/highcharts-more';
    import Accessibility from 'highcharts/modules/accessibility';
    import Boost from 'highcharts/modules/boost';

    let is_mounted = false;
    let is_disabled;
    let chartContainer;
    let channels = [];
    let chart_data = [];
    let y_axis = [];
    let colors = [
        "#7CB5EC",
        "#f7A35C",
        "#31AC0D",
        "#A960AF",
        "#FF0066",
        "#666666",
        "#D1C924",
        "#EEAAEE",
        "#FFCC00",  // Gold
        "#00CCFF"   // Cyan
    ];

    onMount(() => {
        is_mounted = true;
    });

    $: {
        if ($selected_device && $selected_device.box_id) {
            is_disabled = true;
            if ($chart) {
                $chart.destroy();
                $chart = null;
            }
        } else {
            is_disabled = false;
        }
    }

    function is_opposite_axis(measurand) {
        let left_list = ["Volumenstrom", "Druck","Temperatur kalte Seite","Temperatur warme Seite"];
        if (left_list.includes(measurand)) {
            return false;
        }
        return true;
    }

    function update_graph_axis() {
        if ($selected_device) {
            // Base Channels for the graph. since they are not "real" channels in the database
            channels = $extended_device_channel_list;
            // Get unique measurands
            let measurand_set = [
                ...new Set(channels.map((channel) => channel.measurand)),
            ];
            y_axis = channels.map((channel, index) => {
                let is_main_axis = measurand_set.includes(channel.measurand);

                if (is_main_axis) {
                    // Remove the measurand from the set so that the next axis of the same measurand will not be a main axis
                    measurand_set = measurand_set.filter(
                        (measurand) => measurand !== channel.measurand,
                    );
                }
                return {
                    // endOnTick: false,
                    // startOnTick: false,
                    showLastLabel: true,
                    lineColor: colors[index],
                    title: {
                        style: {
                            color: colors[index],
                            fontWeight: "bold",
                        },
                        text: channel.display_name,
                        // margin: -8,
                    },
                    labels: {
                        // formatter: y_axis_formatter,
                        useHTML: true,
                        enabled: true,
                    },
                    opposite: is_opposite_axis(channel.measurand),
                    lineWidth: 2,
                    visible: channel.visible,
                    is_main_axis: is_main_axis,
                    measurand: channel.measurand,
                    index: index,
                };
            });

            // mapping series to yAxis
            chart_data = channels.map((channel, index) => {
                let main_axis = y_axis.find(
                    (axis) =>
                        axis.is_main_axis &&
                        axis.measurand === channel.measurand,
                );

                let series = {
                    lineColor: colors[index],
                    name: channel.display_name,
                    type: "line",
                    data: [],
                    yAxis: main_axis.index,
                    channel_id: channel.channel_id,
                    measurand: channel.measurand,
                    connectNulls: true,
                    visible: channel.visible,
                };
                return series;
            });
            chartOptions.series = chart_data;
            chartOptions.yAxis = y_axis;
            $chart = Highcharts.StockChart(chartContainer, chartOptions);
        }
    }

    function update_graph_data() {
        // Get unique measurands
        let measurand_set = [
            ...new Set(channels.map((channel) => channel.measurand)),
        ];
        y_axis = channels.map((channel, index) => {
            let is_main_axis = measurand_set.includes(channel.measurand);

            if (is_main_axis) {
                // Remove the measurand from the set so that the next axis of the same measurand will not be a main axis
                measurand_set = measurand_set.filter(
                    (measurand) => measurand !== channel.measurand,
                );
            }
            return {
                // endOnTick: false,
                // startOnTick: false,
                showLastLabel: true,
                lineColor: colors[index],
                title: {
                    style: {
                        color: colors[index],
                        fontWeight: "bold",
                    },
                    text: channel.display_name,
                    // margin: -8,
                },
                labels: {
                    // formatter: y_axis_formatter,
                    useHTML: true,
                    enabled: is_main_axis,
                },
                opposite: index % 2 === 1,
                lineWidth: 2,
                visible: channel.visible,
                is_main_axis: is_main_axis,
                measurand: channel.measurand,
            };
        });

        chart_data = channels.map((channel, index) => {
            let device_data = $current_device_data.find(
                (data) => data.config_id == channel.config,
            );
            let main_axis = y_axis.find(
                (axis) =>
                    axis.is_main_axis && axis.measurand === channel.measurand,
            );
            if(channel.config === null){
                device_data = $current_device_data_additional_values;
            } else {
                device_data = device_data ? device_data.data : null;
            }
            let series_data = device_data
                    ? device_data.map((data) => [
                          new Date(data.received_at).getTime(),
                          data[channel.name],
                      ])
                    : []
            let series = {
                lineColor: colors[index],
                name: channel.display_name,
                type: "line",
                data: series_data,
                yAxis: main_axis.index,
                channel_id: channel.channel_id,
                measurand: channel.measurand,
                connectNulls: true,
                visible: channel.visible,
            };
            return series;
        });

        chart_data.forEach((seriesData, index) => {
            if ($chart.series[index]) {
                $chart.series[index].setData(seriesData.data);
            }
        });
    }

    $: {
        if ($selected_device && is_mounted && !is_disabled) {
            update_graph_axis();
        }
    }
    $: {
        if (
            $current_device_data &&
            $current_device_data.length > 0 &&
            is_mounted &&
            !is_disabled
        ) {
            update_graph_data();
        }
    }

    $: if ($chart) {
        let subtitle =
            $current_sample_time === "Echtzeitwerte"
                ? "gemessen"
                : "verdichtet";
        $chart.update({
            title: {
                text: $current_sample_time,
            },
            subtitle: {
                text: subtitle,
            },
        });
    }

    var formatter = function () {
        var xyArr = [];
        xyArr.push(
            Highcharts.dateFormat(
                "%d.%m.%Y %H:%M:%S",
                new Date(this.x),
            ).toLocaleString(),
        );
        xyArr.push(
            '<table><tr>' +
                "<th>Name</th>" +
                '<th><span style="margin-left: 1em">Wert</span></th>' +
                '<th><span style="margin-left: 1em">Min</span></th>' +
                '<th><span style="margin-left: 1em">Max</span></th>' +
                "</tr>",
        );

        this.points.forEach(function (point) {
            var v = Highcharts.numberFormat(
                point.y,
                Math.abs(point.y) < 10000.0 ? 2 : 1,
                ",",
            );
            var min = Highcharts.numberFormat(
                point.series.dataMin,
                Math.abs(point.series.dataMin) < 10000.0 ? 2 : 1,
                ",",
            );
            var max = Highcharts.numberFormat(
                point.series.dataMax,
                Math.abs(point.series.dataMax) < 10000.0 ? 2 : 1,
                ",",
            );

            xyArr.push(
                "<tr>" +
                    '<td><span style="color:' +
                    point.series.color +
                    ';white-space:nowrap;">&#x25CF;&nbsp;' +
                    point.series.name +
                    "</span></td>" +
                    '<td><span style="margin-left:' +
                    1 +
                    'em">' +
                    v +
                    "</span></td>" +
                    '<td><span style="margin-left:' +
                    1 +
                    'em">' +
                    min +
                    "</span></td>" +
                    '<td><span style="margin-left:' +
                    1 +
                    'em">' +
                    max +
                    "</span></td>" +
                    "</tr>",
            );
        });

        xyArr.push("</table>");
        return xyArr.join("");
    };

    Highcharts.setOptions({
        global: {
            useUTC: false,
        },
        lang: {
            decimalPoint: ",",
            thousandsSep: ".",
            loading: "Daten werden geladen...",
            months: [
                "Januar",
                "Februar",
                "M\u00e4rz",
                "April",
                "Mai",
                "Juni",
                "Juli",
                "August",
                "September",
                "Oktober",
                "November",
                "Dezember",
            ],
            weekdays: [
                "Sonntag",
                "Montag",
                "Dienstag",
                "Mittwoch",
                "Donnerstag",
                "Freitag",
                "Samstag",
            ],
            shortMonths: [
                "Jan",
                "Feb",
                "M\u00e4r",
                "Apr",
                "Mai",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Okt",
                "Nov",
                "Dez",
            ],
            exportButtonTitle: "Exportieren",
            printButtonTitle: "Drucken",
            rangeSelectorFrom: "vom",
            rangeSelectorTo: "bis",
            rangeSelectorZoom: "",
            downloadPNG: "Download als PNG-Bild",
            downloadJPEG: "Download als JPEG-Bild",
            downloadPDF: "Download als PDF-Dokument",
            downloadSVG: "Download als SVG-Bild",
            resetZoom: "Zoom zur\u00fccksetzen",
            resetZoomTitle: "Zoom zur\u00fccksetzen",
        },
        colors: colors,
        credits: {
            enabled: false,
        },
        plotOptions: {
            series: {
                allowPointSelect: false,
                cursor: "pointer",
                dataGrouping: {
                    enabled: false,
                    approximation: "sum",
                },
            },
        },
        exporting: {
            buttons: {
                contextButton: {
                    enabled: false,
                },
            },
        },
    });
    var chartOptions = {
        rangeSelector: {
            enabled: false,
        },
        chart: {
            renderTo: "diagram",
            type: "line",
            style: { fontFamily: "Arial, Verdana, sans-serif" },
            backgroundColor: null,
            marginBottom: 20,
        },
        title: {
            text: "Einzelwerte",
            style: {
                fontSize: "16px",
                fontWeight: "bold",
            },
        },
        tooltip: {
            formatter: formatter,
            borderWidth: 0,
            backgroundColor: "rgba(219,219,216,0.8)",
            shadow: false,
            useHTML: true,
            shared: true,
            style: {
                fontFamily: "Arial, Helvetica, Clean, sans-serif",
            },
        },
        xAxis: {
            minPadding: 0.01,
            type: "linear",
            ordinal: false,
            gridLineWidth: 1,
        },
        series: [],
        yAxis: [],
        navigator: {
            margin:30,
        },
        subtitle: {
            text: "gemessen",
        },
    };
</script>

<div class="display_box" bind:this={chartContainer}></div>
